import React from 'react';
import { Form, Input, Select } from 'antd';
import {
    FileOutlined,
    SendOutlined,
} from '@ant-design/icons';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalChromeHeading,
    ModalBlockLabel,
    PanelSectionLabel,
} from '@/pages/inside/tenant/style';
import {
    truncateFileName,
    resolveAttachmentUrl,
    isLikelyImageAttachmentUrl,
    getAttachmentFileExtension,
} from '@hooks/projects/projectHelpers';

export default function IssueUpgradeCreateModal({
    form,
    open,
    onCancel,
    onSubmit,
    submitPending,
    pendingAttachments,
    pendingLinks,
    existingAttachments = [],
    handleFilesSelected,
    removeAttachment,
    handleLinkRequest,
    handleEditorLinkRemoved,
    handleLinkModalSubmit,
    linkModalOpen,
    setLinkModalOpen,
    linkModalForm,
    linkModalCallbackRef,
    mentionUsers,
    mode = 'create',
    modalTitle,
    typeLabel = 'Type',
    showType = true,
    initialType = 'issue',
    descriptionPlaceholder = 'Describe the issue or upgrade. Add files, links, and @mentions if needed.',
}) {
    const isEdit = mode === 'edit';
    const resolvedModalTitle = modalTitle || (isEdit ? 'Edit issue or upgrade' : 'Create issue or upgrade');
    const ExistingAttachmentTile = ({ item }) => {
        const rawUrl = String(item?.url || '').trim();
        const href = resolveAttachmentUrl(rawUrl);
        const title = String(item?.title || rawUrl || 'Attachment').trim();
        const isImage = isLikelyImageAttachmentUrl(rawUrl) || String(item?.type || '').toLowerCase() === 'image';
        const ext = getAttachmentFileExtension(title || rawUrl);
        return (
            <a
                href={href || '#'}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    position: 'relative',
                    borderRadius: 10,
                    overflow: 'hidden',
                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                    background: 'var(--ant-color-fill-tertiary, rgba(0,0,0,0.06))',
                    padding: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    minHeight: 120,
                    textDecoration: 'none',
                    color: 'inherit',
                }}
                title={title}
            >
                {isImage && href ? (
                    <img
                        src={href}
                        alt={title}
                        style={{
                            width: '100%',
                            height: 82,
                            borderRadius: 8,
                            objectFit: 'cover',
                        }}
                    />
                ) : (
                    <div
                        style={{
                            width: '100%',
                            height: 82,
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'var(--ant-color-fill-quaternary, rgba(255,255,255,0.75))',
                            color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                        }}
                    >
                        {(ext || 'FILE').toUpperCase()}
                    </div>
                )}
                <span
                    style={{
                        width: '100%',
                        fontSize: 11,
                        lineHeight: 1.3,
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={title}
                >
                    {truncateFileName(title)}
                </span>
            </a>
        );
    };

    return (
        <>
            <AppModal
                size="md"
                title={<ModalChromeHeading>{resolvedModalTitle}</ModalChromeHeading>}
                open={open}
                onCancel={onCancel}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button $primary={false} $size="14px" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            $primary
                            $blackOnLight
                            $size="14px"
                            loading={submitPending}
                            icon={<SendOutlined />}
                            onClick={onSubmit}
                        >
                            {isEdit ? 'Update' : 'Create'}
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <Form form={form} layout="vertical" requiredMark={false} initialValues={{ type: initialType }}>
                        <Form.Item
                            name="title"
                            label={<ModalBlockLabel>Title</ModalBlockLabel>}
                            rules={[{ required: true, message: 'Enter title' }]}
                        >
                            <Input placeholder="Enter title" size="large" />
                        </Form.Item>
                        {showType ? (
                            <Form.Item
                                name="type"
                                label={<ModalBlockLabel>{typeLabel}</ModalBlockLabel>}
                                rules={[{ required: true, message: 'Select type' }]}
                            >
                                <Select
                                    options={[
                                        { label: 'Issue', value: 'issue' },
                                        { label: 'Upgrade', value: 'upgrade' },
                                    ]}
                                />
                            </Form.Item>
                        ) : null}
                        <Form.Item
                            name="description"
                            label={<ModalBlockLabel>Description</ModalBlockLabel>}
                            rules={[{ required: true, message: 'Enter description' }]}
                        >
                            <RichTextEditor
                                placeholder={descriptionPlaceholder}
                                showAttachButton
                                attachmentIndexOffset={0}
                                mentionUsers={mentionUsers}
                                onFilesSelected={handleFilesSelected}
                                onLinkRequest={handleLinkRequest}
                                onLinkRemoved={handleEditorLinkRemoved}
                                onAttachmentRemoved={(index) => {
                                    const p = pendingAttachments[index];
                                    if (p) removeAttachment(p.id);
                                }}
                            />
                        </Form.Item>
                        {pendingLinks.length > 0 && open ? (
                            <PendingLinkWrap links={pendingLinks} />
                        ) : null}
                        {existingAttachments.length > 0 || pendingAttachments.length > 0 ? (
                            <div style={{ marginTop: 8, marginBottom: 16 }}>
                                <PanelSectionLabel
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        marginBottom: 8,
                                        fontSize: 11,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        opacity: 0.75,
                                    }}
                                >
                                    <FileOutlined />
                                    Attachments
                                </PanelSectionLabel>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                                        gap: 10,
                                    }}
                                >
                                    {existingAttachments.map((x) => (
                                        <ExistingAttachmentTile key={x.id ?? x.url ?? x.title} item={x} />
                                    ))}
                                    {pendingAttachments.map((p) => (
                                        <PendingAttachmentTile
                                            key={p.id}
                                            item={p}
                                            onRemove={() => removeAttachment(p.id)}
                                            truncateFileName={truncateFileName}
                                            showBottomFileName
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </Form>
                </CreateRoleModalBody>
            </AppModal>
            <AppModal
                size="sm"
                title={<ModalChromeHeading>Add link</ModalChromeHeading>}
                open={linkModalOpen}
                onCancel={() => {
                    setLinkModalOpen(false);
                    linkModalCallbackRef.current = null;
                }}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button
                            $primary={false}
                            $size="14px"
                            onClick={() => {
                                setLinkModalOpen(false);
                                linkModalCallbackRef.current = null;
                            }}
                        >
                            Cancel
                        </Button>
                        <Button $primary $blackOnLight $size="14px" icon={<SendOutlined />} onClick={handleLinkModalSubmit}>
                            Add link
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <Form form={linkModalForm} layout="vertical" requiredMark={false}>
                        <Form.Item
                            name="url"
                            label={<ModalBlockLabel>URL</ModalBlockLabel>}
                            rules={[{ required: true, message: 'Enter URL' }]}
                        >
                            <Input placeholder="https://..." size="large" />
                        </Form.Item>
                        <Form.Item name="title" label={<ModalBlockLabel>Link text</ModalBlockLabel>}>
                            <Input placeholder="Optional display text" size="large" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
            </AppModal>
        </>
    );
}
